import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { initTaskFormFormikValues, TaskFormValuesType } from '../../../common/form/task-form/model/task-form-init';
import { deserializeTaskItems } from '../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { RepeatableGroupFormikValue, RepeatableGroupInstance } from './PublicFormView';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { TaskFormFieldList } from '../../../common/form/task-form/TaskFormFieldList';
import { NumberTaskField } from '../../../common/inputs/task-fields/TaskFields';

export const getDefaultCountQuestion = (label: string) => `How many ${label.toLowerCase()}s?`;

interface RepeatableGroupFieldProps {
  formikKey: string;
  groupData: RepeatableGroupFormikValue;
}

export const RepeatableGroupField: React.FC<RepeatableGroupFieldProps> = ({ formikKey, groupData }) => {
  const { setFieldValue, setFieldTouched, values: allValues, errors, touched } = useFormikContext<TaskFormValuesType>();
  const { theme } = useCampfireTheme();
  const {
    title: groupTitle,
    description,
    label,
    minInstances,
    maxInstances,
    groupTaskItems,
    askForCount,
    countQuestion,
  } = groupData;
  const title = groupTitle || label;
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const currentGroupData = (allValues as any)[formikKey] as RepeatableGroupFormikValue;
  const instances = currentGroupData?.instances || [];
  const countError = (errors as any)[formikKey]?.count;
  const groupTouched = (touched as any)[formikKey];
  const countTouched = groupTouched === true || !!groupTouched?.count;

  const childFieldItems = useMemo(
    () => groupTaskItems.filter((item: any) => item.__typename === 'VOLUNTEER_TaskItemFieldType'),
    [groupTaskItems]
  );

  const deserializedGroupItems = useMemo(() => deserializeTaskItems(groupTaskItems as any), [groupTaskItems]);

  const addInstance = useCallback(() => {
    if (instances.length >= maxInstances) return;
    const newInstanceValues = initTaskFormFormikValues(childFieldItems, undefined);
    const newInstance: RepeatableGroupInstance = {
      groupInstanceId: uuidv4(),
      values: newInstanceValues,
    };
    setFieldValue(formikKey, {
      ...currentGroupData,
      instances: [...instances, newInstance],
    });
  }, [instances, maxInstances, childFieldItems, setFieldValue, formikKey, currentGroupData]);

  const removeInstance = useCallback(
    (index: number) => {
      if (instances.length <= minInstances) return;
      setFieldValue(formikKey, {
        ...currentGroupData,
        instances: instances.filter((_unused: any, i: number) => i !== index),
      });
    },
    [instances, minInstances, setFieldValue, formikKey, currentGroupData]
  );

  const resizeInstances = useCallback(
    (targetLength: number) => {
      const nextInstances =
        targetLength <= instances.length
          ? instances.slice(0, targetLength)
          : instances.concat(
              Array.from({ length: targetLength - instances.length }, () => ({
                groupInstanceId: uuidv4(),
                values: initTaskFormFormikValues(childFieldItems, undefined),
              }))
            );
      setFieldValue(formikKey, { ...currentGroupData, count: targetLength, instances: nextInstances });
    },
    [instances, childFieldItems, setFieldValue, formikKey, currentGroupData]
  );

  const handleCountChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const raw = event.target.value;
    const parsed = parseInt(raw, 10);
    if (raw === '' || Number.isNaN(parsed) || parsed < minInstances || parsed > maxInstances) {
      setFieldValue(formikKey, { ...currentGroupData, count: raw === '' ? '' : parsed });
      return;
    }
    const blankValues = JSON.stringify(initTaskFormFormikValues(childFieldItems, undefined));
    const removed = instances.slice(parsed);
    if (removed.some((instance) => JSON.stringify(instance.values) !== blankValues)) {
      setPendingCount(parsed);
      return;
    }
    resizeInstances(parsed);
  };

  const confirmShrink = () => {
    if (pendingCount !== null) resizeInstances(pendingCount);
    setPendingCount(null);
  };

  return (
    <Box mt={3} mb={2}>
      <Box paddingLeft={1} mb={1}>
        <Typography variant='h6'>{title}</Typography>
        {description && (
          <Typography variant='body2' color='textSecondary'>
            {description}
          </Typography>
        )}
      </Box>

      {askForCount && (
        <Box mb={2}>
          <NumberTaskField
            title={countQuestion || getDefaultCountQuestion(label)}
            ariaId={`${formikKey}-count`}
            ariaDescribedBy={`${formikKey}-count-description`}
            required
            value={currentGroupData?.count as number}
            onChange={handleCountChange}
            outlinedInputProps={{
              inputProps: { min: minInstances, max: maxInstances },
              onBlur: () => setFieldTouched(`${formikKey}.count`, true),
            }}
            error={!!countError && !!countTouched}
            helperText={countTouched ? countError : undefined}
          />
        </Box>
      )}

      {instances.map((instance: RepeatableGroupInstance, index: number) => (
        <Box
          key={instance.groupInstanceId}
          mb={2}
          style={{
            border: `1px solid ${theme.color.grey.border}`,
            borderRadius: 4,
            padding: 16,
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
            <Typography variant='body2' color='textSecondary'>
              {label} {index + 1}
            </Typography>
            {!askForCount && instances.length > minInstances && (
              <IconButton size='small' onClick={() => removeInstance(index)}>
                <Close fontSize='small' />
              </IconButton>
            )}
          </Box>

          <TaskFormFieldList
            taskItems={deserializedGroupItems}
            values={instance.values}
            disableAutoFocus
            fieldNamePrefix={`${formikKey}.instances.${index}.values`}
          />
        </Box>
      ))}

      {!askForCount && instances.length < maxInstances && (
        <Box pl={1}>
          <Grid container alignItems='center' spacing={1} style={{ cursor: 'pointer' }} onClick={addInstance}>
            <Grid item>
              <Add color='primary' fontSize='small' />
            </Grid>
            <Grid item>
              <Typography variant='body2' color='primary'>
                {instances.length === 0 ? 'Add' : 'Add another'} {label.toLowerCase()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      <Dialog open={pendingCount !== null} onClose={() => setPendingCount(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Remove entries?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This will remove the last {instances.length - (pendingCount ?? 0)} {label.toLowerCase()} entries and any
            answers entered in them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingCount(null)}>Cancel</Button>
          <Button color='primary' variant='contained' onClick={confirmShrink}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
